import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useGetPainting } from "@/lib/api";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Check,
  Ruler,
  Calendar,
  Share2,
  Tag,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale } from "@/context/locale-context";
import { useCart } from "@/context/cart-context";

export default function PaintingDetail() {
  const { t, formatPrice } = useLocale();
  const { addItem, isInCart } = useCart();
  const { id } = useParams();
  const paintingId = parseInt(id || "0");
  const { data: painting, isLoading, isError } = useGetPainting(paintingId);

  // Estado del carrusel
  const [current, setCurrent] = useState(0);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-12">
        <Skeleton className="h-4 w-24 mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          <Skeleton className="aspect-[4/5] w-full rounded-none" />
          <div className="space-y-6 pt-12">
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !painting) {
    return (
      <div className="container mx-auto px-4 py-32 text-center">
        <h1 className="text-3xl font-serif mb-4">{t.detail.notFound}</h1>
        <p className="text-muted-foreground mb-8">{t.detail.notFoundSubtitle}</p>
        <Link to="/gallery">
          <Button variant="outline" className="rounded-none uppercase tracking-widest">
            {t.detail.backToGallery}
          </Button>
        </Link>
      </div>
    );
  }

  // Parsear las URLs separadas por punto y coma
  const images = painting.imageUrl
    ? painting.imageUrl.split(";").map((u) => u.trim()).filter(Boolean)
    : [];

  const hasManyImages = images.length > 1;

  const goPrev = () => setCurrent((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setCurrent((i) => (i + 1) % images.length);

  return (
    <article className="min-h-screen bg-background" data-testid="painting-detail-page">
      <div className="container mx-auto px-4 md:px-8 py-8">
        <Link
          to="/gallery"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors uppercase tracking-widest mb-8"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="w-4 h-4" /> {t.detail.returnToCatalog}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-24 items-start">
          {/* ── Columna izquierda: imagen / carrusel ── */}
          <div className="lg:col-span-7 lg:sticky lg:top-24">
            <div className="bg-muted relative">
              {images.length > 0 ? (
                <>
                  {/* Imagen activa */}
                  <img
                    src={images[current]}
                    alt={`${painting.title} — foto ${current + 1} de ${images.length}`}
                    className="w-full h-auto object-cover"
                  />

                  {/* Controles del carrusel (solo si hay más de una imagen) */}
                  {hasManyImages && (
                    <>
                      {/* Flecha izquierda */}
                      <button
                        onClick={goPrev}
                        aria-label="Imagen anterior"
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-2 rounded-full shadow transition-colors z-10"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      {/* Flecha derecha */}
                      <button
                        onClick={goNext}
                        aria-label="Imagen siguiente"
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background p-2 rounded-full shadow transition-colors z-10"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>

                      {/* Puntos indicadores */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setCurrent(i)}
                            aria-label={`Ir a imagen ${i + 1}`}
                            className={`w-2 h-2 rounded-full transition-colors ${
                              i === current
                                ? "bg-foreground"
                                : "bg-foreground/30 hover:bg-foreground/60"
                            }`}
                          />
                        ))}
                      </div>

                      {/* Contador (ej: 2 / 4) */}
                      <span className="absolute top-4 right-4 bg-background/80 text-foreground text-xs px-2 py-1 rounded-full z-10">
                        {current + 1} / {images.length}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <AspectRatio ratio={4 / 5} className="flex items-center justify-center bg-muted/50">
                  <span className="font-serif text-2xl text-muted-foreground/50">
                    {t.detail.noImage}
                  </span>
                </AspectRatio>
              )}

              {/* Badges de estado — sin cambios */}
              <div className="absolute top-4 left-4 flex gap-2">
                {painting.sold && (
                  <Badge
                    variant="secondary"
                    className="bg-background/90 text-foreground rounded-none px-4 py-1.5 uppercase tracking-widest text-xs border-none shadow-sm"
                  >
                    {t.detail.inPrivateCollection}
                  </Badge>
                )}
                {!painting.sold && painting.onSale && (
                  <Badge className="bg-accent text-accent-foreground rounded-none px-4 py-1.5 uppercase tracking-widest text-xs shadow-sm">
                    {t.detail.privateSale}
                  </Badge>
                )}
              </div>
            </div>

            {/* Miniaturas (solo si hay más de una imagen) */}
            {hasManyImages && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((url, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`shrink-0 w-16 h-16 overflow-hidden border-2 transition-colors ${
                      i === current
                        ? "border-foreground"
                        : "border-transparent hover:border-muted-foreground"
                    }`}
                  >
                    <img
                      src={url}
                      alt={`Miniatura ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Columna derecha: info ── */}
          <div className="lg:col-span-5 pt-2 lg:pt-12">
            <div className="mb-6 md:mb-8">
              <h1
                className="text-3xl lg:text-5xl font-serif leading-tight mb-3 md:mb-4"
                data-testid="painting-title"
              >
                {painting.title}
              </h1>

              <div className="flex items-baseline gap-4 mb-6">
                {painting.onSale && painting.salePrice ? (
                  <>
                    <span className="text-3xl font-medium">
                      {formatPrice(painting.salePrice)}
                    </span>
                    <span className="text-xl text-muted-foreground line-through">
                      {formatPrice(painting.price)}
                    </span>
                  </>
                ) : (
                  <span className="text-3xl font-medium" data-testid="painting-price">
                    {formatPrice(painting.price)}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {painting.categoryName && (
                  <Link
                    to={`/gallery?categoryId=${painting.categoryId}`}
                    className="inline-block text-sm uppercase tracking-widest text-accent hover:underline underline-offset-4"
                  >
                    {t.detail.category} {painting.categoryName}
                  </Link>
                )}
                {painting.collectionName && (
                  <Link
                    to={`/gallery?collectionId=${painting.collectionId}`}
                    className="inline-block text-sm uppercase tracking-widest text-accent hover:underline underline-offset-4"
                  >
                    {t.detail.collection} {painting.collectionName}
                  </Link>
                )}
              </div>
            </div>

            <Separator className="my-8" />

            <div className="prose prose-neutral font-serif text-lg leading-relaxed mb-8">
              {painting.description ? (
                <p>{painting.description}</p>
              ) : (
                <p className="text-muted-foreground italic">{t.detail.noNotes}</p>
              )}
            </div>

            <ul className="space-y-4 text-sm text-muted-foreground mb-12">
              <li className="flex gap-4 border-b pb-4">
                <Tag className="w-5 h-5 shrink-0 text-foreground" />
                <div>
                  <span className="block font-medium text-foreground uppercase tracking-wider text-xs mb-1">
                    {t.detail.medium}
                  </span>
                  {painting.technique || t.detail.mixedMedia}
                </div>
              </li>
              <li className="flex gap-4 border-b pb-4">
                <Ruler className="w-5 h-5 shrink-0 text-foreground" />
                <div>
                  <span className="block font-medium text-foreground uppercase tracking-wider text-xs mb-1">
                    {t.detail.dimensions}
                  </span>
                  {painting.dimensions || t.detail.dimensionsUnavailable}
                </div>
              </li>
              <li className="flex gap-4 border-b pb-4">
                <Calendar className="w-5 h-5 shrink-0 text-foreground" />
                <div>
                  <span className="block font-medium text-foreground uppercase tracking-wider text-xs mb-1">
                    {t.detail.year}
                  </span>
                  {painting.year || new Date(painting.createdAt).getFullYear()}
                </div>
              </li>
            </ul>

            <div className="space-y-4">
              {painting.sold ? (
                <Button
                  size="lg"
                  className="w-full rounded-none h-14 uppercase tracking-widest font-medium"
                  disabled
                >
                  <Check className="w-4 h-4 mr-2" /> {t.detail.acquired}
                </Button>
              ) : isInCart(painting.id) ? (
                <Link to="/checkout">
                  <Button
                    size="lg"
                    variant="secondary"
                    className="w-full rounded-none h-14 uppercase tracking-widest font-medium"
                    data-testid="go-to-checkout"
                  >
                    <Check className="w-4 h-4 mr-2" /> {t.cart.inCart} — {t.cart.checkout}
                  </Button>
                </Link>
              ) : (
                <Button
                  size="lg"
                  className="w-full rounded-none h-14 uppercase tracking-widest font-medium"
                  onClick={() => addItem(painting)}
                  data-testid="add-to-cart-detail"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" /> {t.cart.addToCart}
                </Button>
              )}

              {!painting.sold && (
                <p className="text-xs text-center text-muted-foreground">
                  {t.detail.shippingNote}
                </p>
              )}
            </div>

            <div className="mt-12 text-center">
              <button className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                <Share2 className="w-4 h-4" /> {t.detail.share}
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}